/**
 * KK-1604: Shared Searchspring identity cookies and Beacon 2.0 tracker.
 *
 * Loaded with defer from the head of theme.liquid, so it runs before every
 * deferred Searchspring module script and before DOMContentLoaded. That
 * ordering is what lets the personalization preflight and the module scripts
 * read the identity cookies on a first page load.
 *
 * Exposes window.SearchspringBeacon. Every consumer call is guarded, and this
 * file never throws into its callers: a failed beacon must not break search,
 * collections or autocomplete.
 */
(function () {
  'use strict';

  var config = window.searchspringBeaconConfig || {};
  var SITE_ID = config.siteId || '8p7msg';
  var BEACON_BASE = 'https://analytics.searchspring.net/beacon/v2/';
  var INITIATOR = 'lineargent/shopify-theme/2.0.0';

  var USER_MAX_AGE = 365 * 24 * 60 * 60;
  var SESSION_MAX_AGE = 30 * 60;
  // The Searchspring cookie reference asks for 7 years. Browsers cap persistent
  // cookie lifetimes well below that, so we send the documented value and treat
  // the shorter effective lifetime as expected rather than a defect.
  var VIEWED_MAX_AGE = 7 * 365 * 24 * 60 * 60;
  var LIST_LIMIT = 20;

  function log() {
    if (!window.searchspringDebug) return;
    var args = Array.prototype.slice.call(arguments);
    console.log.apply(console, ['%c[SearchSpring Beacon]', 'color:#0066cc;font-weight:bold'].concat(args));
  }

  function uuidv4() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      try {
        return window.crypto.randomUUID();
      } catch (error) {
        /* fall through to the manual builder */
      }
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (char) {
      var rand = (Math.random() * 16) | 0;
      return (char === 'x' ? rand : (rand & 0x3) | 0x8).toString(16);
    });
  }

  // The Searchspring Web Pixel writes domain scoped copies of these cookies
  // next to the theme's host only ones, and document.cookie lists both. Prefer
  // the first non empty value so an empty copy never shadows a populated one.
  function readCookie(name) {
    var pattern = new RegExp('(?:^|; ?)' + name + '=([^;]*)', 'g');
    var match;

    while ((match = pattern.exec(document.cookie)) !== null) {
      var value = match[1];
      try {
        value = decodeURIComponent(value);
      } catch (error) {
        /* keep the raw value */
      }
      if (value) return value;
    }

    return '';
  }

  function writeCookie(name, value, maxAgeSeconds) {
    var cookie = name + '=' + encodeURIComponent(value) + ';path=/;SameSite=Lax';
    if (maxAgeSeconds != null) cookie += ';max-age=' + maxAgeSeconds;
    if (window.location.protocol === 'https:') cookie += ';Secure';
    document.cookie = cookie;
  }

  function deleteCookie(name) {
    document.cookie = name + '=;path=/;max-age=0;SameSite=Lax';
  }

  /* Identity ---------------------------------------------------------------- */

  function getUserId() {
    var id = readCookie('ssUserId') || uuidv4();
    writeCookie('ssUserId', id, USER_MAX_AGE);
    return id;
  }

  // Held as a sliding 30 minute window rather than a year, and pushed forward
  // on every tracked event so an active session never expires mid-browse.
  function getSessionId() {
    var id = readCookie('ssSessionId') || uuidv4();
    writeCookie('ssSessionId', id, SESSION_MAX_AGE);
    return id;
  }

  function getPageLoadId() {
    if (!window.__searchspringPageLoadId) window.__searchspringPageLoadId = uuidv4();
    return window.__searchspringPageLoadId;
  }

  function syncShopperId() {
    var shopperId = config.shopperId ? String(config.shopperId) : '';
    if (shopperId) writeCookie('ssShopperId', shopperId, USER_MAX_AGE);
    else deleteCookie('ssShopperId');
    return shopperId;
  }

  function normalizeList(value) {
    return String(value || '')
      .split(',')
      .map(function (entry) { return entry.trim(); })
      .filter(Boolean);
  }

  function setCartProducts(list) {
    var unique = [];
    normalizeList(list).forEach(function (entry) {
      if (unique.indexOf(entry) === -1) unique.push(entry);
    });
    writeCookie('ssCartProducts', unique.slice(0, LIST_LIMIT).join(','), USER_MAX_AGE);
    return unique;
  }

  // Shopify caches Liquid output, so the seeded cart can be stale on a cached
  // page. Refresh from /cart.js once per load as well as on variant:add.
  function refreshCartProducts() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        var skus = (cart.items || []).map(function (item) {
          return item.sku || item.variant_id || '';
        });
        setCartProducts(skus);
        log('ssCartProducts refreshed', readCookie('ssCartProducts'));
      })
      .catch(function (error) { log('cart refresh failed', error); });
  }

  function addViewedProduct(id) {
    if (!id) return readCookie('ssViewedProducts');

    var list = normalizeList(readCookie('ssViewedProducts'));
    var value = String(id);
    var existing = list.indexOf(value);
    if (existing !== -1) list.splice(existing, 1);
    list.unshift(value);

    var next = list.slice(0, LIST_LIMIT).join(',');
    writeCookie('ssViewedProducts', next, VIEWED_MAX_AGE);
    return next;
  }

  /* Environment ------------------------------------------------------------- */

  // The published theme reports role "main". Any other role is a preview or
  // unpublished theme, which must be flagged dev and sent test=true so QA
  // traffic never lands in the client's reporting. The old check looked for a
  // shopifypreview.com hostname, which never fires: our preview runs on the
  // main domain with a preview_theme_id.
  function isDev() {
    try {
      var theme = window.Shopify && window.Shopify.theme;
      if (theme && theme.role) return String(theme.role) !== 'main';
      if (theme && theme.id && config.publishedThemeId) {
        return String(theme.id) !== String(config.publishedThemeId);
      }
    } catch (error) {
      log('theme role unavailable', error);
    }

    // Unknown environment: assume published rather than silently marking real
    // shopper traffic as dev and dropping it from reporting.
    return false;
  }

  function getCurrencyCode() {
    try {
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
        return String(window.Shopify.currency.active);
      }
    } catch (error) {
      /* fall through to the Liquid seeded value */
    }
    return config.currency ? String(config.currency) : 'EUR';
  }

  /* Payload helpers --------------------------------------------------------- */

  function getContext(extra) {
    var context = {
      timestamp: new Date().toISOString(),
      pageUrl: window.location.href,
      userId: getUserId(),
      sessionId: getSessionId(),
      pageLoadId: getPageLoadId(),
      initiator: INITIATOR,
      currency: { code: getCurrencyCode() },
    };

    var shopperId = readCookie('ssShopperId');
    if (shopperId) context.shopperId = shopperId;
    if (isDev()) context.dev = true;

    if (extra) {
      Object.keys(extra).forEach(function (key) { context[key] = extra[key]; });
    }

    return context;
  }

  // Impression, clickthrough and render results. parentId and uid are both the
  // Shopify product id; only add to cart distinguishes the two.
  function mapResult(source, options) {
    var data = source || {};
    var opts = options || {};
    var uid = opts.uid != null ? opts.uid : (data.uid || data.productId || data.id || '');
    var parentId = opts.parentId != null ? opts.parentId : uid;
    var sku = opts.sku != null ? opts.sku : data.sku;

    var result = {
      type: opts.type || 'product',
      parentId: String(parentId || ''),
      uid: String(uid || ''),
    };
    if (sku) result.sku = String(sku);

    return result;
  }

  // Add to cart carries the variant actually added as uid, keeps the product id
  // as parentId, and adds qty and price. The documented payload omits type.
  function mapCartResult(source, options) {
    var data = source || {};
    var opts = options || {};
    var parentId = opts.parentId != null ? opts.parentId : (data.productId || data.id || '');
    var uid = opts.uid != null ? opts.uid : (data.variantId || parentId);
    var sku = opts.sku != null ? opts.sku : data.sku;
    var price = opts.price != null ? opts.price : data.price;

    var result = {
      parentId: String(parentId || ''),
      uid: String(uid || ''),
      qty: Number(opts.qty != null ? opts.qty : 1) || 1,
    };
    if (price != null && price !== '' && isFinite(Number(price))) result.price = Number(price);
    if (sku) result.sku = String(sku);

    return result;
  }

  /* Transport --------------------------------------------------------------- */

  function track(module, event, data) {
    try {
      if (!SITE_ID || !module || !event) return Promise.resolve(false);

      var url = BEACON_BASE + encodeURIComponent(SITE_ID) + '/' + encodeURIComponent(module) + '/' + encodeURIComponent(event);
      var payload = { context: getContext(), data: data || {} };

      log('track', module + '/' + event, payload);

      // Default cors mode: analytics.searchspring.net returns
      // Access-Control-Allow-Origin *, so we can read the status back and log a
      // failure instead of firing blind like the old no-cors 1.0 call did.
      return fetch(url, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify(payload),
      })
        .then(function (response) {
          if (!response.ok) log('track rejected', module + '/' + event, response.status);
          return response.ok;
        })
        .catch(function (error) {
          log('track failed', module + '/' + event, error);
          return false;
        });
    } catch (error) {
      log('track threw', error);
      return Promise.resolve(false);
    }
  }

  /* Personalization and API params ------------------------------------------ */

  function getPersonalizationParams() {
    return {
      shopper: readCookie('ssShopperId') || undefined,
      cart: readCookie('ssCartProducts') || undefined,
      lastViewed: readCookie('ssViewedProducts') || undefined,
    };
  }

  // beacon=true is required on every Searchspring API call at all times.
  // test=true is added only on a non published theme.
  function applyApiParams(urlParams) {
    if (!urlParams || typeof urlParams.set !== 'function') return urlParams;
    urlParams.set('beacon', 'true');
    if (isDev()) urlParams.set('test', 'true');
    return urlParams;
  }

  /* Recommendations --------------------------------------------------------- */

  // KK-1608: shared by the related products section and the cross sell embed.
  // Both render one Searchspring profile through a Shopify section, so the
  // caller passes the raw recommend response, its profile tag and the container
  // the rendered cards live in. Cards are matched back to results by handle,
  // because the rendered product-card carries only its handle.
  function getProfiles(data) {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.profiles)) return data.profiles;
    if (data && data.profiles && typeof data.profiles === 'object') return Object.values(data.profiles);
    return data ? [data] : [];
  }

  function getResultHandle(result) {
    var core = (result && result.mappings && result.mappings.core) || {};
    var match = String(core.url || (result && result.url) || '').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  function bindRecommendations(container, data, tag) {
    try {
      if (!container || !data) return false;

      var profiles = getProfiles(data);
      var profile = null;
      profiles.forEach(function (entry) {
        if (!profile && entry && entry.profile && entry.profile.tag === tag) profile = entry;
      });
      profile = profile || profiles[0];
      if (!profile || !profile.responseId) return false;

      var profileTag = String((profile.profile && profile.profile.tag) || tag || '');
      var responseId = String(profile.responseId);
      var byHandle = {};
      (profile.results || []).forEach(function (result) {
        var handle = getResultHandle(result);
        var core = (result && result.mappings && result.mappings.core) || {};
        var uid = String(result.id || core.uid || '');
        if (handle && uid) {
          byHandle[handle] = {
            uid: uid,
            sku: String(core.sku || result.sku || ''),
            price: core.price != null ? String(core.price) : '',
          };
        }
      });

      var cards = [];
      Array.prototype.forEach.call(container.querySelectorAll('product-card[handle]'), function (card) {
        var match = byHandle[card.getAttribute('handle')];
        if (!match) return;
        card.setAttribute('data-ss-product-id', match.uid);
        card.setAttribute('data-ss-uid', match.uid);
        card.setAttribute('data-ss-sku', match.sku);
        card.setAttribute('data-ss-price', match.price);
        card.setAttribute('data-ss-response-id', responseId);
        card.setAttribute('data-ss-tag', profileTag);
        cards.push(card);
      });

      track('recommendations', 'render', { tag: profileTag, responseId: responseId });

      // Impressions count only cards actually in the viewport, so carousel
      // slides scrolled out of view are reported once they come into view.
      if (container.__ssRecommendationObserver) container.__ssRecommendationObserver.disconnect();
      var impressed = {};
      var observer = new IntersectionObserver(function (entries) {
        var results = [];
        entries.forEach(function (entry) {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.4) return;
          var card = entry.target;
          var key = responseId + ':' + card.getAttribute('data-ss-product-id');
          if (impressed[key]) return;
          impressed[key] = true;
          results.push(mapResult({ uid: card.getAttribute('data-ss-product-id'), sku: card.getAttribute('data-ss-sku') }));
        });
        if (results.length) {
          track('recommendations', 'impression', { tag: profileTag, responseId: responseId, results: results, banners: [] });
        }
      }, { threshold: [0.4] });
      cards.forEach(function (card) { observer.observe(card); });
      container.__ssRecommendationObserver = observer;

      if (!container.__ssRecommendationsBound) {
        container.__ssRecommendationsBound = true;

        container.addEventListener('click', function (event) {
          var link = event.target && event.target.closest ? event.target.closest('a[href]') : null;
          var card = link ? link.closest('product-card[data-ss-response-id]') : null;
          if (!card) return;
          track('recommendations', 'clickthrough', {
            tag: card.getAttribute('data-ss-tag'),
            responseId: card.getAttribute('data-ss-response-id'),
            results: [mapResult({ uid: card.getAttribute('data-ss-product-id'), sku: card.getAttribute('data-ss-sku') })],
          });
        });

        // Quick add is the submit button of the theme's product form inside the
        // card. The theme handles the add on that click and no submit event
        // reaches the block, so listen in the capture phase, which nothing
        // below can stop, and report the variant the form posts, its quantity
        // and the result's price.
        container.addEventListener('click', function (event) {
          var button = event.target && event.target.closest ? event.target.closest('form[action*="/cart/add"] button[type="submit"]') : null;
          var form = button ? button.closest('form') : null;
          var card = form ? form.closest('product-card[data-ss-response-id]') : null;
          if (!card) return;
          var variantInput = form.querySelector('input[name="id"]');
          var qtyInput = form.querySelector('input[name="quantity"]');
          if (!variantInput || !variantInput.value) return;
          track('recommendations', 'addtocart', {
            tag: card.getAttribute('data-ss-tag'),
            responseId: card.getAttribute('data-ss-response-id'),
            results: [mapCartResult({
              productId: card.getAttribute('data-ss-product-id'),
              variantId: variantInput.value,
              sku: card.getAttribute('data-ss-sku'),
              price: card.getAttribute('data-ss-price') || undefined,
            }, { qty: Number(qtyInput && qtyInput.value) || 1 })],
          });
        }, true);
      }

      return true;
    } catch (error) {
      log('bindRecommendations failed', error);
      return false;
    }
  }

  /* Bootstrap --------------------------------------------------------------- */

  function bootstrap() {
    getUserId();
    getSessionId();
    syncShopperId();

    if (config.cartProducts != null) setCartProducts(config.cartProducts);
    else if (!readCookie('ssCartProducts')) setCartProducts('');

    if (!readCookie('ssViewedProducts')) writeCookie('ssViewedProducts', '', VIEWED_MAX_AGE);

    log('cookies bootstrapped', {
      ssUserId: readCookie('ssUserId'),
      ssSessionId: readCookie('ssSessionId'),
      ssShopperId: readCookie('ssShopperId'),
      ssCartProducts: readCookie('ssCartProducts'),
      ssViewedProducts: readCookie('ssViewedProducts'),
      dev: isDev(),
    });

    refreshCartProducts();
    document.addEventListener('variant:add', refreshCartProducts);
  }

  window.SearchspringBeacon = {
    track: track,
    getContext: getContext,
    mapResult: mapResult,
    mapCartResult: mapCartResult,
    getPageLoadId: getPageLoadId,
    getUserId: getUserId,
    getSessionId: getSessionId,
    getPersonalizationParams: getPersonalizationParams,
    applyApiParams: applyApiParams,
    bindRecommendations: bindRecommendations,
    addViewedProduct: addViewedProduct,
    setCartProducts: setCartProducts,
    refreshCartProducts: refreshCartProducts,
    isDev: isDev,
    siteId: SITE_ID,
  };

  try {
    bootstrap();
  } catch (error) {
    log('bootstrap failed', error);
  }
})();
